/**
 * Fonte unica de verdade para decisoes responsivas em JS.
 *
 * Antes desta auditoria (docs/12-auditoria-mobile-professor.md, achado C1) o
 * projeto tinha QUATRO implementacoes de "estou no mobile?", com dois
 * criterios diferentes:
 *   - Dashboard.js e TeacherAnalyticsDashboard.jsx: window.innerWidth < 768
 *     com listener de `resize` (dispara a cada pixel arrastado)
 *   - AiUsageModal.jsx: matchMedia("(hover: none) and (pointer: coarse)")
 *   - CSS dos modais do professor: 1200px / 900px / 820px
 *
 * O resultado era uma FAIXA MORTA entre 768px e 1200px (iPad em retrato,
 * Android grande em paisagem): o CSS ja tinha colapsado o layout pra uma
 * coluna, mas o JS ainda dizia "desktop", entao os controles renderizavam na
 * densidade densa de desktop dentro de um layout estreito.
 *
 * Dois conceitos DISTINTOS sao expostos aqui de proposito — boa parte das
 * decisoes do codigo antigo dizia "largura" quando na verdade queria
 * "capacidade de toque" (ex.: desligar tooltip de grafico):
 *
 *   useIsNarrow() -> tenho pouco espaco horizontal? (layout)
 *   useIsTouch()  -> o dedo e o ponteiro principal? (interacao)
 *
 * Um notebook com tela sensivel ao toque responde `false` no primeiro e
 * `true` no segundo — e essa diferenca importa.
 *
 * Os valores abaixo sao os MESMOS usados nos CSS Modules. Ao mudar um,
 * mude o outro (busque pelo comentario "BP_" nos .module.css).
 */
import { useEffect, useState } from "react";

/** Abaixo disto: layout de coluna unica, controles condensados. */
export const BP_MOBILE = 768;

/** Entre BP_MOBILE e BP_TABLET: coluna unica, mas com folga. */
export const BP_TABLET = 1024;

/**
 * Assina uma media query e devolve se ela casa no momento.
 * Usa matchMedia + evento `change` (dispara so na TRANSICAO do breakpoint),
 * nao `resize` (dispara a cada pixel).
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);

    // Resincroniza: a query pode ter mudado entre o useState inicial e aqui.
    setMatches(mql.matches);

    // Safari < 14 nao tem addEventListener em MediaQueryList — o app suporta
    // iOS antigo (ver docs de PWA), entao o fallback deprecado fica.
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

/** Pouco espaco horizontal — decisoes de LAYOUT. */
export function useIsNarrow() {
  return useMediaQuery(`(max-width: ${BP_MOBILE - 1}px)`);
}

/** Faixa de tablet: coluna unica, mas ainda com espaco. */
export function useIsTablet() {
  return useMediaQuery(
    `(min-width: ${BP_MOBILE}px) and (max-width: ${BP_TABLET - 1}px)`
  );
}

/** O dedo e o ponteiro principal — decisoes de INTERACAO (hover, tooltip). */
export function useIsTouch() {
  return useMediaQuery("(hover: none) and (pointer: coarse)");
}
